/**
 * Sawyer 08 meds-feeding (A2+G10) — the med pass: today's doses for one time of
 * day, signed one tap at a time on the floor tablet.
 *
 * Sawyer (memo3): "by law … when they administer the medication …
 * initial", with the statuses "Prepared, refused, partially administered,
 * administered", and the AM crew prepares while the PM crew completes. One
 * tap = one POST = one row under the tapper's own login and the server's
 * clock (no confirm modal: #7). The row then shows what the RECORD says,
 * from the server's answer, never local optimism (#5). A correction is
 * another tap; the history keeps every entry (append-only, #553).
 *
 * Doses can be recorded only for today and only for dogs checked in
 * (refuter C-3): an expected arrival reads "Not checked in yet", a dog that
 * went home reads so, and doses already recorded always show. As-needed
 * medications sit in their own section on every slot view (G10). Dogs whose
 * medication is only text are listed with "Set up meds" for writers, and
 * every dog has "Edit meds & feeding" for writers (the kennel tech cannot
 * open the profile; refuter C-8).
 *
 * Five states: loading, empty ("No doses due at AM today."), per-row saving,
 * per-row error ("Couldn't save. Check the connection and tap again."), and
 * page error with Retry. Tablet (768) first; at 375 the buttons wrap 2×2 and
 * the slot switcher scrolls inside itself, never the page.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  appendDose,
  type CareDayDog,
  type CareDayResponse,
  type DoseStatusView,
  type MedPassOutcome,
  type StaffMedicationView,
} from '../../lib/meds-feeding-api.js';
import { slotLabel } from '../../lib/care-labels.js';
import { MedsFeedingEditor } from './MedsFeedingEditor.js';
import { editorDogFrom, MedicationLine, useCareDay } from './MedsFeedingReport.js';
import { doseStatusText, indexCareDay, longDateLabel, prnText, visitTag } from './care-day-format.js';
import './meds-feeding.css';

export const PASS_SLOTS = ['am', 'midday', 'pm', 'bedtime', 'as_needed'] as const;
export type PassSlot = (typeof PASS_SLOTS)[number];

const SLOT_TAB_LABEL: Record<PassSlot, string> = {
  am: 'AM',
  midday: 'Noon',
  pm: 'PM',
  bedtime: 'Bedtime',
  as_needed: 'PRN',
};

const FINAL_OUTCOMES = new Set(['given', 'partially_administered', 'refused', 'missed']);

const BUTTONS: ReadonlyArray<{ outcome: MedPassOutcome; label: string; note?: string }> = [
  { outcome: 'prepared', label: 'Prepared' },
  { outcome: 'given', label: 'Administered' },
  { outcome: 'partially_administered', label: 'Partly given', note: 'How much was given?' },
  { outcome: 'refused', label: 'Refused', note: 'Anything to note? (optional)' },
];

export interface PassRow {
  dog: CareDayDog;
  med: StaffMedicationView;
  slot: PassSlot;
  status: DoseStatusView;
}

/** Done = a final outcome is the latest entry (Prepared is not done). */
export function isDone(status: DoseStatusView): boolean {
  return status.latest !== null && FINAL_OUTCOMES.has(status.latest.outcome);
}

/** Rows for one slot, in dog order. Exported for tests. */
export function rowsFor(day: CareDayResponse, slot: PassSlot): PassRow[] {
  const idx = indexCareDay(day);
  const group = day.groups.find((g) => g.slot === slot);
  if (!group) return [];
  const rows: PassRow[] = [];
  for (const e of group.entries) {
    const dog = idx.dogs.get(e.dogId);
    if (!dog) continue;
    for (const d of e.doses) {
      const med = idx.meds.get(d.medicationId);
      if (med) rows.push({ dog, med, slot, status: d.status });
    }
  }
  return rows;
}

/** "3 of 7 done" per scheduled slot. */
export function slotProgress(day: CareDayResponse, slot: PassSlot): { done: number; total: number } {
  const rows = rowsFor(day, slot);
  return { done: rows.filter((r) => isDone(r.status)).length, total: rows.length };
}

/** The first scheduled slot that still has an open dose today (no clock guess); else AM. */
export function defaultSlot(day: CareDayResponse): PassSlot {
  for (const s of ['am', 'midday', 'pm', 'bedtime'] as const) {
    const p = slotProgress(day, s);
    if (p.total > p.done) return s;
  }
  return 'am';
}

/** Replace one dose's status in the payload with what the server answered. */
function withStatus(day: CareDayResponse, row: PassRow, status: DoseStatusView): CareDayResponse {
  return {
    ...day,
    groups: day.groups.map((g) =>
      g.slot !== row.slot
        ? g
        : {
            ...g,
            entries: g.entries.map((e) =>
              e.dogId !== row.dog.dogId
                ? e
                : { ...e, doses: e.doses.map((d) => (d.medicationId === row.med.id ? { ...d, status } : d)) },
            ),
          },
    ),
  };
}

export interface MedPassListProps {
  apiUrl: string;
  date: string;
  slot: PassSlot | null;
  onSlotChange: (slot: PassSlot) => void;
  dateControl: JSX.Element;
}

export function MedPassList({ apiUrl, date, slot, onSlotChange, dateControl }: MedPassListProps): JSX.Element {
  const { state, load, setState } = useCareDay(apiUrl, date, 'all');
  const [editing, setEditing] = useState<CareDayDog | null>(null);
  const data = state.kind === 'ready' ? state.data : null;
  const active: PassSlot = slot ?? (data ? defaultSlot(data) : 'am');
  // Pin the opening slot once the day loads: otherwise finishing the last AM
  // dose would recompute the default and yank the tablet over to PM.
  useEffect(() => {
    if (slot === null && data) onSlotChange(defaultSlot(data));
  }, [slot, data, onSlotChange]);

  return (
    <div className="ferro-mf-pass" data-testid="med-pass">
      <div className="ferro-mf-toolbar">
        <div className="ferro-mf-toolbar__row">
          <div className="ferro-mf-toolbar__title">
            <h1 className="ferro-mf-title">Med pass</h1>
            {data?.facilityName ? <p className="ferro-mf-toolbar__facility">{data.facilityName}</p> : null}
          </div>
          <Link className="ferro-btn ferro-btn--ghost ferro-mf-btn" to={`/meds-feeding?date=${date}&service=all`} data-testid="med-pass-to-list">
            Meds &amp; feeding list →
          </Link>
        </div>
        <div className="ferro-mf-toolbar__row">{dateControl}</div>
        <div className="ferro-mf-slots" role="tablist" aria-label="Time of day" data-testid="med-pass-slots">
          {PASS_SLOTS.map((s) => {
            const p = data && s !== 'as_needed' ? slotProgress(data, s) : null;
            return (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={active === s}
                className="ferro-mf-slot"
                onClick={() => onSlotChange(s)}
                data-testid={`med-pass-slot-${s}`}
              >
                <span className="ferro-mf-slot__label">{SLOT_TAB_LABEL[s]}</span>
                {p ? (
                  <span className="ferro-mf-slot__count">
                    {p.done} of {p.total} done
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div className="ferro-mf-state" role="status" aria-live="polite" data-testid="med-pass-loading">
          <p className="ferro-mf-state__text">Loading the med pass…</p>
          <span className="ferro-mf-skeleton" aria-hidden="true" />
          <span className="ferro-mf-skeleton ferro-mf-skeleton--short" aria-hidden="true" />
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-mf-error" role="alert" data-testid="med-pass-error">
          <p className="ferro-mf-error__text">Couldn&apos;t load the med pass.</p>
          <p className="ferro-mf-state__text">Check the connection, then try again.</p>
          <div className="ferro-mf-actions">
            <button type="button" className="ferro-btn ferro-btn--outline ferro-mf-btn" onClick={() => void load()}>
              Retry
            </button>
          </div>
        </div>
      ) : null}

      {data ? (
        <PassBody
          apiUrl={apiUrl}
          data={data}
          slot={active}
          onRecorded={(row, status) => setState({ kind: 'ready', data: withStatus(data, row, status) })}
          onStale={() => void load({ quiet: true })}
          onEdit={setEditing}
        />
      ) : null}

      {editing ? (
        <MedsFeedingEditor
          apiUrl={apiUrl}
          dog={editorDogFrom(editing)}
          footnote="Past medications are on the dog profile."
          onClose={() => setEditing(null)}
          onChanged={() => void load({ quiet: true })}
        />
      ) : null}
    </div>
  );
}

function PassBody({
  apiUrl,
  data,
  slot,
  onRecorded,
  onStale,
  onEdit,
}: {
  apiUrl: string;
  data: CareDayResponse;
  slot: PassSlot;
  onRecorded: (row: PassRow, status: DoseStatusView) => void;
  onStale: () => void;
  onEdit: (d: CareDayDog) => void;
}): JSX.Element {
  const rows = useMemo(() => rowsFor(data, slot), [data, slot]);
  const prnRows = useMemo(() => (slot === 'as_needed' ? [] : rowsFor(data, 'as_needed')), [data, slot]);
  const idx = useMemo(() => indexCareDay(data), [data]);
  const when = data.isToday ? 'today' : `on ${longDateLabel(data.date)}`;
  const seen = new Set<string>();
  const firstFor = (dogId: string) => {
    if (seen.has(dogId)) return false;
    seen.add(dogId);
    return true;
  };

  const rowProps = { apiUrl, data, onRecorded, onStale, onEdit, firstFor };

  return (
    <>
      {!data.isToday ? (
        <p className="ferro-mf-banner" role="status" data-testid="med-pass-not-today">
          Doses are recorded on the day they are given. This is the record for {longDateLabel(data.date)}.
        </p>
      ) : null}

      <section className="ferro-mf-group" aria-labelledby="med-pass-main">
        <h2 id="med-pass-main" className="ferro-mf-group__title">
          {slotLabel(slot)}
        </h2>
        {rows.length === 0 ? (
          <p className="ferro-mf-empty" data-testid="med-pass-empty">
            {slot === 'as_needed' ? `No as-needed medications ${when}.` : `No doses due at ${slotLabel(slot)} ${when}.`}
          </p>
        ) : (
          <ul className="ferro-mf-entries">
            {rows.map((r) => (
              <DoseRow key={`${r.dog.dogId}-${r.med.id}`} row={r} prn={slot === 'as_needed'} {...rowProps} />
            ))}
          </ul>
        )}
      </section>

      {prnRows.length > 0 ? (
        <section className="ferro-mf-group" aria-labelledby="med-pass-prn" data-testid="med-pass-prn">
          <h2 id="med-pass-prn" className="ferro-mf-group__title">
            As needed (PRN)
          </h2>
          <ul className="ferro-mf-entries">
            {prnRows.map((r) => (
              <DoseRow key={`prn-${r.dog.dogId}-${r.med.id}`} row={r} prn {...rowProps} />
            ))}
          </ul>
        </section>
      ) : null}

      {data.notesOnly.length > 0 ? (
        <section className="ferro-mf-group" aria-labelledby="med-pass-notes" data-testid="med-pass-notes-only">
          <h2 id="med-pass-notes" className="ferro-mf-group__title">
            Meds on file as notes only
          </h2>
          <ul className="ferro-mf-entries">
            {data.notesOnly.map((dogId) => {
              const d = idx.dogs.get(dogId);
              if (!d) return null;
              return (
                <li key={dogId} className="ferro-mf-entry">
                  <span className="ferro-mf-dogname">{d.name}</span>
                  <p className="ferro-mf-entry__notes">{d.medicationNotes}</p>
                  {data.canEditMedsFeeding ? (
                    <div className="ferro-mf-actions">
                      <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => onEdit(d)}>
                        Set up meds
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function DoseRow({
  row,
  prn,
  apiUrl,
  data,
  onRecorded,
  onStale,
  onEdit,
  firstFor,
}: {
  row: PassRow;
  prn: boolean;
  apiUrl: string;
  data: CareDayResponse;
  onRecorded: (row: PassRow, status: DoseStatusView) => void;
  onStale: () => void;
  onEdit: (d: CareDayDog) => void;
  firstFor: (dogId: string) => boolean;
}): JSX.Element {
  const [noteFor, setNoteFor] = useState<(typeof BUTTONS)[number] | null>(null);
  const [saving, setSaving] = useState<MedPassOutcome | null>(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState('');
  const [correcting, setCorrecting] = useState(false);
  const tz = data.timeZone;
  const { dog, med, status } = row;
  const first = firstFor(dog.dogId);

  const canRecord = data.canLogDoses && data.isToday && dog.visitStatus === 'checked_in';
  const done = isDone(status);
  const showButtons = canRecord && noteFor === null && (prn || !done || correcting);
  const buttons = BUTTONS.filter((b) => {
    if (prn) return b.outcome === 'given';
    // Prepared only while nothing has been recorded for this dose.
    if (b.outcome === 'prepared') return status.latest === null;
    return true;
  });

  const record = async (outcome: MedPassOutcome, notes?: string) => {
    setSaving(outcome);
    setFailed(false);
    const res = await appendDose(apiUrl, {
      dogId: dog.dogId,
      medicationId: med.id,
      slot: row.slot,
      outcome,
      bookingId: dog.bookingId,
      ...(notes && notes.trim() ? { notes: notes.trim() } : {}),
    });
    setSaving(null);
    if (res.data) {
      setNoteFor(null);
      setNote('');
      setCorrecting(false);
      if (res.data.dose.status) onRecorded(row, res.data.dose.status);
      else onStale();
      return;
    }
    // Not recorded: the row stays open and the button is live again.
    setFailed(true);
  };

  const onTap = (b: (typeof BUTTONS)[number]) => {
    if (b.note && !prn) {
      setNoteFor(b);
      return;
    }
    void record(b.outcome);
  };

  const statusText = prn ? prnText(status, tz) : doseStatusText(status, tz);
  const idle =
    dog.visitStatus === 'expected'
      ? 'Not checked in yet'
      : dog.visitStatus === 'checked_out'
        ? 'Went home'
        : canRecord
          ? 'Not recorded yet'
          : 'Nothing recorded';

  return (
    <li className="ferro-mf-entry ferro-mf-doserow" data-testid={`med-pass-row-${dog.dogId}-${med.id}`}>
      <div className="ferro-mf-entry__head">
        <span className="ferro-mf-dogname">{dog.name}</span>
        <span className="ferro-mf-entry__meta">{[dog.breed, visitTag(dog, tz)].filter(Boolean).join(' · ')}</span>
      </div>
      {dog.allergies ? (
        <p className="ferro-mf-allergy">
          <strong>Allergies:</strong> {dog.allergies}
        </p>
      ) : null}
      <MedicationLine med={med} />
      <p className="ferro-mf-doserow__status" data-testid="med-pass-status" aria-live="polite">
        {statusText ?? idle}
      </p>

      {showButtons ? (
        <div className="ferro-mf-dosebtns" role="group" aria-label={`Record ${med.name} for ${dog.name}`}>
          {buttons.map((b) => (
            <button
              key={b.outcome}
              type="button"
              className={`ferro-mf-dosebtn ferro-mf-dosebtn--${b.outcome}`}
              disabled={saving !== null}
              onClick={() => onTap(b)}
              data-testid={`med-pass-${b.outcome}`}
            >
              {saving === b.outcome ? 'Saving…' : prn ? 'Record a dose' : b.label}
            </button>
          ))}
        </div>
      ) : null}

      {canRecord && done && !prn && !correcting && noteFor === null ? (
        <div className="ferro-mf-actions">
          <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => setCorrecting(true)} data-testid="med-pass-correct">
            Correct
          </button>
        </div>
      ) : null}

      {noteFor ? (
        <div className="ferro-mf-note">
          <label className="ferro-mf-label" htmlFor={`note-${dog.dogId}-${med.id}`}>
            {noteFor.label}: {noteFor.note}
          </label>
          <input
            id={`note-${dog.dogId}-${med.id}`}
            className="ferro-mf-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={saving !== null}
            maxLength={500}
            data-testid="med-pass-note"
          />
          <div className="ferro-mf-actions">
            <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" disabled={saving !== null} onClick={() => setNoteFor(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary ferro-mf-btn"
              disabled={saving !== null}
              onClick={() => void record(noteFor.outcome, note)}
              data-testid="med-pass-note-save"
            >
              {saving !== null ? 'Saving…' : `Save ${noteFor.label.toLowerCase()}`}
            </button>
          </div>
        </div>
      ) : null}

      {failed ? (
        <p className="ferro-mf-field__error" role="alert" data-testid="med-pass-row-error">
          Couldn&apos;t save. Check the connection and tap again.
        </p>
      ) : null}

      {status.history.length > 0 ? (
        <details className="ferro-mf-history">
          <summary className="ferro-mf-history__summary">History ({status.history.length})</summary>
          <ol className="ferro-mf-history__list">
            {status.history.map((h, i) => {
              const corrected = status.history.slice(i + 1).some((older) => FINAL_OUTCOMES.has(older.outcome));
              return (
                <li key={h.id}>
                  {doseStatusText({ latest: h, history: [], countToday: 1 }, tz)}
                  {corrected ? ' · Corrected' : ''}
                  {h.notes ? ` — ${h.notes}` : ''}
                </li>
              );
            })}
          </ol>
        </details>
      ) : null}

      {first && data.canEditMedsFeeding ? (
        <div className="ferro-mf-actions">
          <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => onEdit(dog)} data-testid={`med-pass-edit-${dog.dogId}`}>
            Edit meds &amp; feeding
          </button>
        </div>
      ) : null}
    </li>
  );
}

